import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Flex, Box } from 'reflexbox';
import { ButtonCircle, Text } from 'rebass';

import SimcInput from 'shared/simcinput';

import config from 'frontend/config';

import actions from 'frontend/state/actions';

import { selectors } from 'frontend/state/reducers/simbot/simcExpertOptions';

import {
  Checkbox,
  DarkerBox,
  Icon,
  If,
  Textarea,
} from 'frontend/components';


const SimcExpertOptions = ({
  simcExpertMode,
  header,
  preActors,
  raidActors,
  postActors,
  footer,

  toggleSimcExpertMode,
  inputHeader,
  inputPreActors,
  inputRaidActors,
  inputPostActors,
  inputFooter,
}) => {
  const inputs = [{
    name: 'header',
    input: header,
  }, {
    name: 'preActors',
    input: preActors,
  }, {
    name: 'raidActors',
    input: raidActors,
  }, {
    name: 'postActors',
    input: postActors,
  }, {
    name: 'footer',
    input: footer,
  }];

  const invalidCommands = {};
  _.each(inputs, ({ name, input }) => {
    const data = SimcInput.parse(input);
    invalidCommands[name] = SimcInput.getInvalidAdvancedCommands(data);
  });

  const styleStripes = (color1, color2) => (
    `repeating-linear-gradient(-45deg, ${color1}, ${color1} 10px, ${color2} 10px, ${color2} 20px)`
  );

  return (
    <Box>
      <Checkbox
        name="simcExpertMode"
        label="SimC Expert Mode"
        checked={simcExpertMode}
        onChange={toggleSimcExpertMode}
      />
      <If
        condition={simcExpertMode}
        then={() => (
          <DarkerBox
            style={{
              background: styleStripes('#292929', '#323232'),
            }}
          >
            <Flex align="center" my={1} mr={2} style={{ lineHeight: 1 }}>
              <Box>
                <ButtonCircle
                  size={20}
                  color={config.colors.black}
                  backgroundColor={config.colors.error}
                  style={{
                    minHeight: '20px',
                  }}
                  mr={1}
                >
                  <Icon name="warning" size={14} />
                </ButtonCircle>
              </Box>
              <Text size={14} color={config.colors.error} bold>
                Expert Mode assumes you understand SimC input and/or can debug your own issues. Seriallos will not
                help you with this mode.
              </Text>
            </Flex>
            <Text small>
              <a
                href="https://support.raidbots.com/article/15-simc-expert-mode"
                target="_blank"
                rel="noopener noreferrer"
              >
                Expert Mode Reference
              </a>
            </Text>

            <Textarea
              name="header"
              label="Header"
              message="Injected before the Base Actor"
              value={header}
              onChange={e => inputHeader(e.target.value)}
              rows={5}
              mt={1}
              mb={0}
            />
            <If
              condition={invalidCommands.header.length > 0}
              then={() => (
                <Text color={config.colors.red} small bold mt={0}>
                  Invalid Header options: {_.map(invalidCommands.header, 'key').join(', ')}
                </Text>
              )}
            />

            <Textarea
              name="preActor"
              label="Base Player Customization"
              message="Injected between Base Actor Custom APL and Actors. Player scope will modify the base player"
              value={preActors}
              onChange={e => inputPreActors(e.target.value)}
              rows={5}
              mt={1}
              mb={0}
            />
            <If
              condition={invalidCommands.preActors.length > 0}
              then={() => (
                <Text color={config.colors.red} small bold mt={0}>
                  Invalid Pre-Actors options: {_.map(invalidCommands.preActors, 'key').join(', ')}
                </Text>
              )}
            />

            <Textarea
              name="raidActor"
              label="Raid Actors"
              message={
                'Extremely experimental! Injected after base character and before Actors. '
                + 'When used, single_actor_batch is disabled'
              }
              value={raidActors}
              onChange={e => inputRaidActors(e.target.value)}
              rows={5}
              mt={1}
              mb={0}
            />
            <If
              condition={invalidCommands.raidActors.length > 0}
              then={() => (
                <Text color={config.colors.red} small bold mt={0}>
                  Invalid Raid Actors options: {_.map(invalidCommands.raidActors, 'key').join(', ')}
                </Text>
              )}
            />

            <Textarea
              name="postActors"
              label="Post Combination Actors"
              message="Injected between Actors and Custom Enemy"
              value={postActors}
              onChange={e => inputPostActors(e.target.value)}
              rows={5}
              mt={1}
              mb={0}
            />
            <If
              condition={invalidCommands.postActors.length > 0}
              then={() => (
                <Text color={config.colors.red} small bold mt={0}>
                  Invalid Post-Actors options: {_.map(invalidCommands.postActors, 'key').join(', ')}
                </Text>
              )}
            />

            <Textarea
              name="footer"
              label="Footer"
              message="Injected after Simulation Options"
              value={footer}
              onChange={e => inputFooter(e.target.value)}
              rows={5}
              mt={1}
              mb={0}
            />
            <If
              condition={invalidCommands.footer.length > 0}
              then={() => (
                <Text color={config.colors.red} small bold mt={0}>
                  Invalid Footer options: {_.map(invalidCommands.footer, 'key').join(', ')}
                </Text>
              )}
            />
          </DarkerBox>
        )}
      />
    </Box>
  );
};

SimcExpertOptions.propTypes = {
  simcExpertMode: PropTypes.bool.isRequired,
  header: PropTypes.string,
  preActors: PropTypes.string,
  raidActors: PropTypes.string,
  postActors: PropTypes.string,
  footer: PropTypes.string,

  toggleSimcExpertMode: PropTypes.func.isRequired,
  inputHeader: PropTypes.func.isRequired,
  inputPreActors: PropTypes.func.isRequired,
  inputRaidActors: PropTypes.func.isRequired,
  inputPostActors: PropTypes.func.isRequired,
  inputFooter: PropTypes.func.isRequired,
};

SimcExpertOptions.defaultProps = {
  header: '',
  preActors: '',
  raidActors: '',
  postActors: '',
  footer: '',
};

const mapStateToProps = (state, props) => ({
  simcExpertMode: selectors.simcExpertMode(state, props),

  header: selectors.header(state, props),
  preActors: selectors.preActors(state, props),
  raidActors: selectors.raidActors(state, props),
  postActors: selectors.postActors(state, props),
  footer: selectors.footer(state, props),
});

const mapDispatchToProps = {
  toggleSimcExpertMode: actions.toggleSimcExpertMode,
  inputHeader: actions.inputExpertHeader,
  inputPreActors: actions.inputExpertPreActors,
  inputRaidActors: actions.inputExpertRaidActors,
  inputPostActors: actions.inputExpertPostActors,
  inputFooter: actions.inputExpertFooter,
};

export default connect(mapStateToProps, mapDispatchToProps)(SimcExpertOptions);
